import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LinkIcon from '@mui/icons-material/Link';
import { useGetWorkflowExecution, useStartWorkflow } from '../../clients/graphql/pages/workflow-queries';

export interface WorkflowStep {
  executionId: string;
  stepName: string;
  startedAt?: string;
  endAt?: string;
  inputPayload?: any;
  outputResults?: any;
  executionStatus?: {
    isRunning: boolean;
    lastHeartbeatAt?: string;
  };
}

export interface WorkflowExecution {
  executionId: string;
  startedAt?: string;
  endAt?: string;
  steps: WorkflowStep[];
}

export interface StepRenderer {
  (step: WorkflowStep, input?: any, output?: any): {
    text: string;
    tooltip?: string;
  };
}

export interface SherlockWorkflowDrawerProps {
  open: boolean;
  onClose: () => void;
  portfolioKey: number;
  workflowExecutionId?: string;
  stepRenderers?: Record<string, StepRenderer>;
  defaultStepRenderer?: StepRenderer;
  onWorkflowStarted?: (executionId: string) => void;
}

const defaultStepRenderer: StepRenderer = (step) => ({
  text: `${step.stepName}`,
  tooltip: `Execution ID: ${step.executionId}`,
});

const getStepStatus = (step: WorkflowStep): 'pending' | 'running' | 'completed' | 'failed' => {
  if (step.endAt) {
    // Check if step failed
    const hasError = step.outputResults?.error || 
      (typeof step.outputResults === 'object' && step.outputResults?.success === false);
    return hasError ? 'failed' : 'completed';
  }
  
  if (step.startedAt || step.executionStatus?.isRunning) {
    return 'running';
  }
  
  return 'pending';
};

const getStatusIcon = (status: 'pending' | 'running' | 'completed' | 'failed') => {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon />;
    case 'failed':
      return <ErrorIcon />;
    case 'running':
      return <CircularProgress size={20} />;
    case 'pending':
      return <HourglassEmptyIcon />;
  }
};

const getStatusColor = (status: 'pending' | 'running' | 'completed' | 'failed') => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
      return 'primary';
    case 'pending':
      return 'grey';
  }
};

export const SherlockWorkflowDrawer: React.FC<SherlockWorkflowDrawerProps> = ({
  open,
  onClose,
  portfolioKey,
  workflowExecutionId,
  stepRenderers = {},
  defaultStepRenderer: customDefaultRenderer,
  onWorkflowStarted,
}) => {
  const [executionData, setExecutionData] = useState<WorkflowExecution | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isStartingWorkflow, setIsStartingWorkflow] = useState(false);

  const { data, loading, error: queryError, startPolling, stopPolling } = useGetWorkflowExecution({
    variables: { executionId: workflowExecutionId || '' },
    skip: !workflowExecutionId || !open,
    fetchPolicy: 'network-only',
  });

  const [startWorkflow] = useStartWorkflow();

  useEffect(() => {
    if (data?.workflowExecutionBigTable) {
      setExecutionData(data.workflowExecutionBigTable);
      setError(null);
    } else if (queryError) {
      setError('Failed to fetch workflow execution data');
    }
  }, [data, queryError]);

  // Start polling when workflow execution is active
  useEffect(() => {
    if (workflowExecutionId && open) {
      startPolling(2000); // Poll every 2 seconds
    } else {
      stopPolling();
    }
    
    return () => stopPolling();
  }, [workflowExecutionId, open, startPolling, stopPolling]);

  // Filter out root spans and get only actual workflow steps
  const getWorkflowSteps = (execution: WorkflowExecution): WorkflowStep[] => {
    return execution.steps.filter(step => 
      step.stepName !== 'root' && 
      step.stepName !== 'rootSpan' &&
      step.stepName !== 'workflow'
    );
  };

  const renderStepContent = (step: WorkflowStep) => {
    const renderer = stepRenderers[step.stepName] || customDefaultRenderer || defaultStepRenderer;
    
    try {
      const result = renderer(step, step.inputPayload, step.outputResults);
      return result;
    } catch (err) {
      console.error('Error rendering step:', err);
      return {
        text: `${step.stepName} (Render Error)`,
        tooltip: `Error rendering step: ${err}`,
      };
    }
  };

  const handleClose = () => {
    setExecutionData(null);
    setError(null);
    onClose();
  };

  const handleStartInvestigation = async () => {
    try {
      setIsStartingWorkflow(true);
      setError(null);
      
      const { data: startData } = await startWorkflow({
        variables: {
          input: {
            workflowName: 'BalancingFractionalRemoverWorkflow',
            payload: {
              portfolioKey: portfolioKey,
            },
          },
        },
      });

      if (startData?.startWorkflow?.success) {
        const newExecutionId = startData.startWorkflow.workflowId;
        onWorkflowStarted?.(newExecutionId);
        // The drawer will automatically refresh with the new execution ID
      } else {
        setError('Failed to start workflow investigation');
      }
    } catch (err) {
      console.error('Failed to start workflow:', err);
      setError(`Failed to start investigation: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsStartingWorkflow(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: '60%',
          minWidth: '600px',
          boxSizing: 'border-box',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderLeft: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
          marginTop: '64px', // Account for AppBar height
          height: 'calc(100% - 64px)', // Adjust height to account for AppBar
        },
      }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src="/sherlock-logo.png"
              alt="Sherlock"
              sx={{ width: '1.5rem', height: '1.5rem' }}
            />
            <Typography variant="h6" fontWeight={600}>
              Fix Fractionals
            </Typography>
            <Typography variant="caption" color="text.secondary">
              #{portfolioKey}
            </Typography>
            {workflowExecutionId && (
              <Link
                href={`/developer/workflows/BalancingFractionalRemoverWorkflow/executions/${workflowExecutionId}`}
                target="_blank"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                <LinkIcon sx={{ fontSize: '0.75rem', mr: 0.25 }} />
                workflow
              </Link>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!workflowExecutionId && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PlayArrowIcon />}
                onClick={handleStartInvestigation}
                disabled={isStartingWorkflow}
                sx={{ 
                  backgroundColor: '#2c3e50', 
                  '&:hover': { backgroundColor: '#34495e' },
                  '&:disabled': { backgroundColor: '#bdc3c7' },
                  fontSize: '0.75rem',
                  px: 1.5,
                  py: 0.5
                }}
              >
                {isStartingWorkflow ? 'Starting...' : 'Start'}
              </Button>
            )}
            <IconButton onClick={handleClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {executionData && (
            <Box>
              <List sx={{ width: '100%', py: 0 }}>
                {getWorkflowSteps(executionData).map((step, index) => {
                  const status = getStepStatus(step);
                  const content = renderStepContent(step);
                  
                  return (
                    <React.Fragment key={step.executionId}>
                      <ListItem sx={{ alignItems: 'flex-start', py: 1.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              backgroundColor: getStatusColor(status) === 'success' ? '#4caf50' :
                                              getStatusColor(status) === 'error' ? '#f44336' :
                                              getStatusColor(status) === 'primary' ? '#2196f3' : '#9e9e9e',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                            }}
                          >
                            {getStatusIcon(status)}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography 
                              variant="body2" 
                              fontWeight={500}
                            >
                              {content.text}
                            </Typography>
                          }
                          secondary={
                            content.tooltip && (
                              <Typography variant="caption" color="text.secondary">
                                {content.tooltip}
                              </Typography>
                            )
                          }
                        />
                      </ListItem>
                      {index < getWorkflowSteps(executionData).length - 1 && (
                        <Divider sx={{ ml: 4, mr: 0 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
          )}

          {!loading && !executionData && !error && (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Click "Start" to begin investigation
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
